// Activo Fijo → Movimientos → Ventas de activos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, HandCoins, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { ActivoRow, Campo, Modal, Opcion, VentaRow, afRpc, fieldCls, listarProveedores, money } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const hoy = () => fechaLocal();

export function VentasAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<VentaRow[]>({ queryKey: ['af-ventas'], queryFn: () => afRpc<VentaRow[]>('af_ventas') });
  const { data: activos = [] } = useQuery<ActivoRow[]>({ queryKey: ['af-activos', 'activo', ''], queryFn: () => afRpc<ActivoRow[]>('af_activos', { _estado: 'activo', _busq: null }) });
  const { data: compradores = [] } = useQuery<Opcion[]>({ queryKey: ['fin-proveedores'], queryFn: listarProveedores, staleTime: 60_000 });
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ activo_id: '', fecha: hoy(), precio: 0, comprador_id: '', factura_ref: '', observaciones: '' });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));
  const sel = useMemo(() => activos.find((a) => a.id === form.activo_id), [activos, form.activo_id]);
  const utilidad = sel ? Math.round((Number(form.precio) - sel.valor_libros) * 100) / 100 : 0;

  const registrar = useMutation({
    mutationFn: () => afRpc<any>('af_venta_registrar', { _activo_id: form.activo_id, _fecha: form.fecha, _precio: Number(form.precio) || 0, _comprador_id: form.comprador_id || null, _factura_ref: form.factura_ref || null, _observaciones: form.observaciones || null }),
    onSuccess: (r) => {
      const u = Number(r.utilidad);
      const txt = u >= 0 ? `utilidad ${money(u)}` : `pérdida ${money(-u)}`;
      if (r.contabilizada) toast.success(`Venta registrada · ${txt} · asiento generado`);
      else toast.warning(`Venta registrada (${txt}) SIN asiento: ${r.aviso ?? 'revisa Mapeo contable'}`);
      setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-ventas'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => afRpc('af_venta_anular', { _venta_id: id }),
    onSuccess: () => { toast.success('Venta anulada · activo restaurado'); void qc.invalidateQueries({ queryKey: ['af-ventas'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><HandCoins className="w-5 h-5" /> Ventas de activos</h1>
        <Button onClick={() => { setForm({ activo_id: '', fecha: hoy(), precio: 0, comprador_id: '', factura_ref: '', observaciones: '' }); setAbierto(true); }} disabled={activos.length === 0}><Plus className="w-4 h-4 mr-1" /> Registrar venta</Button>
      </div>
      <p className="text-sm text-muted-foreground">Disposición por venta: compara el precio con el valor en libros y reconoce utilidad o pérdida. El ingreso del precio va a una cuenta puente para conciliar con la factura de venta en Financiero (evita doble ingreso).</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Ventas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th><th className="py-2 pr-3">Comprador</th><th className="py-2 pr-3 text-right">Precio</th><th className="py-2 pr-3 text-right">Valor en libros</th><th className="py-2 pr-3 text-right">Utilidad/Pérdida</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin ventas</td></tr>}
              {rows.map((v) => (
                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{v.fecha}</td>
                  <td className="py-2 pr-3 font-mono">{v.placa}</td>
                  <td className="py-2 pr-3">{v.activo}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{v.comprador ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(v.precio_venta)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(v.valor_en_libros)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${v.utilidad >= 0 ? 'text-estable' : 'text-red-600 dark:text-red-400'}`}>{money(v.utilidad)}</td>
                  <td className="py-2 pr-3">{v.estado === 'anulada' ? <span className="text-xs text-muted-foreground">anulada</span> : <span className="rounded-full bg-pendiente-soft text-pendiente px-2 py-0.5 text-xs">registrada</span>}</td>
                  <td className="py-2 text-right">{v.estado === 'registrada' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular" onClick={async () => { if (await dlg.confirmar(`¿Anular la venta de ${v.placa}?`, { detalle: 'El activo volverá a estar activo.', peligro: true })) anular.mutate(v.id); }}><Ban className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar venta" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Activo *">
              <select className={fieldCls} value={form.activo_id} onChange={(e) => set({ activo_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {activos.map((a) => <option key={a.id} value={a.id}>{a.placa} · {a.nombre} (valor en libros {money(a.valor_libros)})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label="Precio de venta *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.precio} onChange={(e) => set({ precio: Number(e.target.value) })} /></Campo>
            </div>
            {sel && (
              <p className="text-sm rounded-md border p-2 bg-muted/20">
                Valor en libros {money(sel.valor_libros)} · Precio {money(Number(form.precio))} →{' '}
                {utilidad >= 0 ? <span className="text-estable font-medium">utilidad {money(utilidad)}</span> : <span className="text-red-600 dark:text-red-400 font-medium">pérdida {money(-utilidad)}</span>}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Comprador"><select className={fieldCls} value={form.comprador_id} onChange={(e) => set({ comprador_id: e.target.value })}><option value="">—</option>{compradores.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}</select></Campo>
              <Campo label="Factura ref."><input className={fieldCls} value={form.factura_ref} onChange={(e) => set({ factura_ref: e.target.value })} /></Campo>
            </div>
            <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={form.observaciones} onChange={(e) => set({ observaciones: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={!form.activo_id} onClick={() => registrar.mutate()}>Registrar venta</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
